import { Alert, Box, Button } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useArgusOnboarding } from "../../hooks/argus/use-argus-onboarding";
import { useOrgFlags } from "../../hooks/use-org-flags";
import { ArgusOnboardingHero } from "./argus-onboarding-hero";
import { OnboardingHeader } from "./onboarding-header";

/**
 * Full-screen Argus onboarding shell. Mounts outside the dashboard layout
 * (Phase 2 route registration) so the hero owns the full viewport.
 *
 * Responsibilities:
 *   1. Drive provision + status polling via `useArgusOnboarding`.
 *   2. Render `<ArgusOnboardingHero>` with the derived phase.
 *   3. CTA → navigate to `/argus?session=<id>&sessions=0&artifacts=0`.
 *   4. Skip → patch `flags.onboarding` then navigate home. If the PATCH
 *      rejects we stay on this page and surface an inline retry banner,
 *      otherwise the dashboard would bounce us right back here.
 *
 * There is **no auto-transition** — the user's click on the CTA is the
 * explicit entry into the Argus workspace.
 */
export function ArgusOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { agentSessionId, instanceReady, sessionReady, provisionError, retryProvision } =
    useArgusOnboarding();
  const { patchFlags, isPatching } = useOrgFlags();
  // Track elapsed time while we're waiting on the sandbox — piped to the
  // hero's tooltip + status pill.
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    if (instanceReady && sessionReady) return;
    const started = Date.now();
    const id = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - started) / 1000));
    }, 1_000);
    return () => window.clearInterval(id);
  }, [instanceReady, sessionReady]);

  // Dev-only visual inspection hatches — guard on Vite's DEV flag so the
  // URL knobs are no-ops in prod.
  const devForcePhase = useMemo<"provisioning" | "booting" | "ready" | "error" | null>(() => {
    if (!import.meta.env.DEV) return null;
    if (searchParams.get("argusForceError") === "1") return "error";
    if (searchParams.get("argusForceReady") === "1") return "ready";
    if (searchParams.get("argusForceBooting") === "1") return "booting";
    return null;
  }, [searchParams]);

  // Derive the phase from hook state. Hero only supports 3 phases
  // (`booting | ready | error`); "provisioning" maps to `booting` visually
  // because they're both "spinner + disabled CTA".
  //
  // `ready` REQUIRES `agentSessionId` to be non-null — the CTA target URL
  // (`/argus?session=<agentSessionId>`) is malformed otherwise, so the
  // button must stay disabled until we have the gateway id even if
  // `sessionReady` already flipped on a prior poll.
  const derivedPhase: "booting" | "ready" | "error" = (() => {
    if (devForcePhase === "provisioning") return "booting";
    if (devForcePhase) return devForcePhase;
    if (provisionError) return "error";
    if (instanceReady && sessionReady && agentSessionId) return "ready";
    return "booting";
  })();

  const [skipError, setSkipError] = useState<Error | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const handleStart = () => {
    // Navigate with the gateway's `agent_session_id` (not the DB row PK) —
    // `/argus` looks up the session via `agent_session_id` and its SSE
    // subscription only matches the gateway id. `sessionReady` implies
    // this is non-null, but guard defensively.
    if (!agentSessionId) return;
    navigate(`/argus?session=${agentSessionId}&sessions=0&artifacts=0`);
  };

  const handleRetry = () => {
    setIsRetrying(true);
    retryProvision();
    // Let the next status poll clear the error; we flip the spinner off
    // after ~1s so the Retry button animates consistently whether the
    // mutation resolves fast or slow.
    window.setTimeout(() => setIsRetrying(false), 1_000);
  };

  const handleHeroClick = () => {
    if (derivedPhase === "error") {
      handleRetry();
      return;
    }
    if (derivedPhase === "ready") {
      handleStart();
    }
  };

  const handleSkip = async () => {
    setSkipError(null);
    try {
      await patchFlags({
        onboarding: {
          completed_at: new Date().toISOString(),
          skipped: true,
          completion_reason: "skipped",
        },
      });
      navigate("/", { replace: true });
    } catch (err) {
      // Don't navigate until the flag durably writes — otherwise the
      // dashboard's onboarding-redirect guard would boomerang us back here.
      setSkipError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        fontFamily:
          '"Geist", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
      }}
    >
      {/* 80px flex-center wrapper mirrors `<SplitPanel.Header>` in
          BetterOnboarding so switching flows doesn't visually shift the
          header by a few pixels. */}
      <Box sx={{ height: 80, display: "flex", alignItems: "center", flexShrink: 0 }}>
        <OnboardingHeader onSkip={handleSkip} skipDisabled={isPatching} />
      </Box>

      {/* Inline skip-error banner — only appears if the PATCH rejected.
          Keeps the user on this page; Retry just re-fires `handleSkip`. */}
      {skipError && (
        <Box sx={{ px: { xs: 3, md: 6 }, pb: 1 }}>
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={handleSkip} disabled={isPatching}>
                Try again
              </Button>
            }
          >
            Couldn't skip right now — {skipError.message || "please try again"}.
          </Alert>
        </Box>
      )}

      {/* Hero fills the remaining space. */}
      <Box sx={{ flex: 1, display: "flex" }}>
        <ArgusOnboardingHero
          phase={derivedPhase}
          onStart={handleHeroClick}
          elapsedSeconds={elapsedSeconds}
          isRetrying={isRetrying}
        />
      </Box>
    </Box>
  );
}
